import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Eyebrow, PageLoader } from "@/components/shared";

export default function Pricing() {
  const [plans, setPlans] = useState(null);
  const [annual, setAnnual] = useState(false);
  const nav = useNavigate();

  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data)); }, []);
  if (!plans) return <PageLoader />;

  return (
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
      <div className="text-center">
        <Eyebrow>Pricing</Eyebrow>
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">One predictable monthly price.</h1>
        <p className="mx-auto mt-4 max-w-2xl text-slate-400">Every plan includes unlimited Proxmox nodes, unlimited backup jobs, TLS encryption, verification, monitoring and alerts. You pay for reserved storage — dedup savings are yours.</p>
        <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-slate-800 bg-slate-900/60 px-5 py-2.5">
          <span className={`text-sm ${!annual ? "text-slate-100" : "text-slate-500"}`}>Monthly</span>
          <Switch checked={annual} onCheckedChange={setAnnual} data-testid="billing-cycle-toggle" />
          <span className={`text-sm ${annual ? "text-slate-100" : "text-slate-500"}`}>Annual</span>
          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-300">2 months free</span>
        </div>
      </div>

      <div className="mt-14 grid gap-5 lg:grid-cols-4">
        {plans.map((p) => {
          const highlight = p.key === "business";
          const price = annual ? p.price_annual : p.price_monthly;
          return (
            <div key={p.key} data-testid={`plan-card-${p.key}`}
              className={`relative flex flex-col rounded-2xl border p-6 ${highlight ? "border-cyan-500/40 bg-gradient-to-b from-cyan-500/5 to-transparent zk-glow" : "border-slate-800 bg-slate-900/40"}`}>
              {highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-cyan-400 px-3 py-0.5 text-xs font-semibold text-[#06080D]">Most popular</div>}
              <div className="font-display text-lg font-bold text-slate-100">{p.name}</div>
              <div className="mt-1 font-mono text-sm text-cyan-300">{p.storage_tb} TB storage</div>
              <div className="mt-5 flex items-end gap-1">
                <span className="font-display text-4xl font-extrabold text-slate-50">${annual ? Math.round(price / 12) : price}</span>
                <span className="mb-1 text-sm text-slate-500">/mo</span>
              </div>
              {annual && <div className="mt-1 text-xs text-slate-500">${price} billed annually</div>}
              <ul className="mt-6 flex-1 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-400">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => nav("/signup")} data-testid={`plan-cta-${p.key}`}
                className={`mt-6 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all ${highlight ? "bg-cyan-400 text-[#06080D] hover:bg-cyan-300" : "border border-slate-700 text-slate-200 hover:border-slate-500"}`}>
                Deploy Now <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-10 rounded-2xl border border-slate-800 bg-slate-900/40 p-6 text-center">
        <p className="text-slate-300">Need more than 10 TB? <span className="text-cyan-300">Custom quotes available.</span> Optional storage overage is <span className="font-mono text-slate-200">$20 / TB-month</span> so a busy month never stops your backups.</p>
        <p className="mt-2 text-sm text-slate-500">Not sure? Start small and expand at any time — no re-provisioning required.</p>
      </div>
    </div>
  );
}
